/**
 * Parent Dashboard - Simplified dashboard for parents with key actions
 * 
 * Features:
 * - Family Profile card (active, navigates to profile page)
 * - Inbox card (active, navigates to inbox page)
 * - Events card (coming soon, disabled)
 * 
 * Used in combination with updated Header navigation that shows only:
 * - Profile
 * - Dashboard
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import Section from '../components/Section';
import Card from '../components/Card';
import Button from '../components/Button';
import LoadingState from '../components/LoadingState';
import { useAuth } from '../contexts/AuthContext';
import { Mail, FileText } from 'lucide-react';

const ParentDashboard = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect if not authenticated (but only after auth check is complete)
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
      return;
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Don't render if auth is still loading or not authenticated
  if (authLoading) {
    return (
      <PageLayout maxWidth="1400px">
        <LoadingState />
      </PageLayout>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <PageLayout maxWidth="1400px">
      <PageHeader
        title={`WELCOME, ${user?.first_name?.toUpperCase() || user?.email?.split('@')[0]?.toUpperCase() || 'PARENT'}`}
        subtitle="Your family soccer dashboard"
        style={{ marginBottom: '2rem' }}
      />

      {/* Dashboard Cards Grid */}
      <Section>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {/* Inbox Card - Active */}
          <Card 
            level={2}
            style={{
              minHeight: '220px',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              border: '1px solid var(--color-border, rgba(0, 0, 0, 0.1))',
              borderRadius: '12px'
            }}
          >
            <div style={{
              padding: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              flex: 1
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                background: 'rgba(var(--color-primary-rgb, 66, 135, 245), 0.1)',
                color: 'var(--color-primary, #4287f5)'
              }}>
                <Mail size={32} />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: 'var(--color-text)',
                  margin: '0 0 0.25rem 0',
                  lineHeight: 1.3
                }}>Inbox</h3>
                <p style={{
                  fontSize: '0.9rem',
                  color: 'var(--color-text-secondary)',
                  margin: 0,
                  lineHeight: 1.4
                }}>
                  Messages and notifications from clubs and coaches
                </p>
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                alignItems: 'stretch',
                marginTop: 'auto',
                paddingTop: '0.75rem',
                borderTop: '1px solid var(--color-border, rgba(0, 0, 0, 0.05))'
              }}>
                <Button
                  variant="primary"
                  onClick={() => navigate('/inbox')}
                  style={{ width: '100%' }}
                >
                  View Messages
                </Button>
              </div>
            </div>
          </Card>

          {/* Documents Card - Active */}
          <Card 
            level={2}
            style={{
              minHeight: '220px',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              border: '1px solid var(--color-border, rgba(0, 0, 0, 0.1))',
              borderRadius: '12px'
            }}
          >
            <div style={{
              padding: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              flex: 1
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                background: 'rgba(var(--color-primary-rgb, 66, 135, 245), 0.1)',
                color: 'var(--color-primary, #4287f5)'
              }}>
                <FileText size={32} />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: 'var(--color-text)',
                  margin: '0 0 0.25rem 0',
                  lineHeight: 1.3
                }}>Documents</h3>
                <p style={{
                  fontSize: '0.9rem',
                  color: 'var(--color-text-secondary)',
                  margin: 0,
                  lineHeight: 1.4
                }}>
                  View and complete required document assignments
                </p>
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                alignItems: 'stretch',
                marginTop: 'auto',
                paddingTop: '0.75rem',
                borderTop: '1px solid var(--color-border, rgba(0, 0, 0, 0.05))'
              }}>
                <Button
                  variant="primary"
                  onClick={() => navigate('/my-documents')}
                  style={{ width: '100%' }}
                >
                  Go to Documents
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </Section>
    </PageLayout>
  );
};

export default ParentDashboard;
